import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, ShareIcon, ArrowPathIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';

interface ChapterInfo {
  chapter_number: number;
  chapter_name: string;
  page_count: number;
}

interface BookWithChapters {
  id: string;
  title: string;
  author: string;
  category: string;
  progress_percentage: number;
  chapters: ChapterInfo[];
}

interface SmartNoteInfo {
  id: string;
  book_id: string;
  chapter_number: number;
  status: string;
  url?: string;
  created_at: string;
}

export default function Playbook() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [books, setBooks] = useState<BookWithChapters[]>([]);
  const [selectedBookId, setSelectedBookId] = useState<string>('');
  const [generatedNotes, setGeneratedNotes] = useState<Record<string, Record<number, SmartNoteInfo>>>({});
  const [generatingKey, setGeneratingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch all books and their chapters
  useEffect(() => {
    const fetchData = async () => {
      try {
        const booksRes = await api.get('/api/books/');
        const rawBooks = Array.isArray(booksRes.data) ? booksRes.data : (booksRes.data.books || []);

        // NOTE: book.chapters from the API is a JSONB object, NOT an array — don't use it
        const booksWithEmptyChapters: BookWithChapters[] = rawBooks.map((book: any) => ({
          id: book.id?.toString() || book.uuid || '',
          title: book.title || 'Untitled',
          author: book.author || '',
          category: book.category || '',
          progress_percentage: book.progress_percentage || 0,
          chapters: [] as ChapterInfo[],
        }));
        setBooks(booksWithEmptyChapters);
        if (booksWithEmptyChapters.length > 0) {
          setSelectedBookId(booksWithEmptyChapters[0].id);
        }

        // Fetch chapters for each book in parallel
        const updatedBooks = await Promise.all(
          booksWithEmptyChapters.map(async (book) => {
            try {
              const chaptersRes = await api.get(`/api/books/${book.id}/chapters`);
              const chaptersObj = chaptersRes.data.chapters || {};
              const chaptersArray: ChapterInfo[] = Object.entries(chaptersObj)
                .map(([num, data]: [string, any]) => ({
                  chapter_number: parseInt(num, 10),
                  chapter_name: data?.name || `Chapter ${num}`,
                  page_count: 0,
                }))
                .sort((a, b) => a.chapter_number - b.chapter_number);
              return { ...book, chapters: chaptersArray };
            } catch {
              return book;
            }
          })
        );
        setBooks(updatedBooks);

        // Fetch all existing smart notes
        const notesRes = await api.get('/api/users/me/smart-notes');
        const notesByBookChapter: Record<string, Record<number, SmartNoteInfo>> = {};
        for (const note of notesRes.data) {
          if (!notesByBookChapter[note.book_id]) {
            notesByBookChapter[note.book_id] = {};
          }
          notesByBookChapter[note.book_id][note.chapter_number] = note;

          // Resume polling for in-progress generations
          if (note.status === 'generating' || note.status === 'pending') {
            const genKey = `${note.book_id}-${note.chapter_number}`;
            setGeneratingKey(genKey);
            const poll = setInterval(async () => {
              try {
                const res = await api.get(`/api/books/${note.book_id}/chapters/${note.chapter_number}/smart-notes/status`);
                if (res.data.status === 'completed') {
                  clearInterval(poll);
                  setGeneratingKey(null);
                  const result = await api.get(`/api/books/${note.book_id}/chapters/${note.chapter_number}/smart-notes`);
                  setGeneratedNotes(prev => ({
                    ...prev,
                    [note.book_id]: {
                      ...(prev[note.book_id] || {}),
                      [note.chapter_number]: result.data,
                    },
                  }));
                } else if (res.data.status === 'failed') {
                  clearInterval(poll);
                  setGeneratingKey(null);
                }
              } catch {
                // Keep polling
              }
            }, 3000);
          }
        }
        setGeneratedNotes(notesByBookChapter);
      } catch (err) {
        console.error('Failed to load playbook data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleGenerate = useCallback(async (bookId: string, chapterNumber: number) => {
    const key = `${bookId}-${chapterNumber}`;
    setGeneratingKey(key);

    try {
      await api.post(`/api/books/${bookId}/chapters/${chapterNumber}/smart-notes`);

      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/api/books/${bookId}/chapters/${chapterNumber}/smart-notes/status`);
          if (res.data.status === 'completed') {
            clearInterval(poll);
            setGeneratingKey(null);
            const result = await api.get(`/api/books/${bookId}/chapters/${chapterNumber}/smart-notes`);
            setGeneratedNotes(prev => ({
              ...prev,
              [bookId]: {
                ...(prev[bookId] || {}),
                [chapterNumber]: result.data,
              },
            }));
          } else if (res.data.status === 'failed') {
            clearInterval(poll);
            setGeneratingKey(null);
          }
        } catch {
          // Keep polling
        }
      }, 3000);
    } catch (err) {
      console.error('Failed to generate:', err);
      setGeneratingKey(null);
    }
  }, []);

  const handleView = (note: SmartNoteInfo) => {
    if (note?.url) {
      window.open(note.url, '_blank');
    } else {
      navigate(`/playbook/view/${note.id}`);
    }
  };

  const handleShare = (note: SmartNoteInfo, title: string) => {
    const shareUrl = note?.url || `${window.location.origin}/playbook/view/${note.id}`;
    const text = encodeURIComponent(`Check out my playbook for ${title}: ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const selectedBook = useMemo(
    () => books.find(b => b.id === selectedBookId),
    [books, selectedBookId]
  );

  const chapters = useMemo(
    () => (selectedBook && Array.isArray(selectedBook.chapters)) ? selectedBook.chapters : [],
    [selectedBook]
  );

  const readyCount = useMemo(() => {
    if (!selectedBookId) return 0;
    const bookNotes = generatedNotes[selectedBookId];
    if (!bookNotes) return 0;
    return Object.values(bookNotes).filter(n => n.status === 'completed').length;
  }, [generatedNotes, selectedBookId]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6 md:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <SparklesIcon className="h-6 w-6 text-amber-500 flex-shrink-0" />
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
            Your Playbook
          </h1>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <ArrowPathIcon className="h-6 w-6 text-slate-400 animate-spin" />
          </div>
        )}

        {/* Empty — no books */}
        {!loading && books.length === 0 && (
          <div className="text-center py-20">
            <SparklesIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              Start reading to unlock your Playbook
            </p>
            <button
              onClick={() => navigate('/library')}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors"
            >
              Go to Library
            </button>
          </div>
        )}

        {/* Main content */}
        {!loading && books.length > 0 && (
          <>
            {/* Book selector dropdown */}
            <div className="mb-2">
              <select
                value={selectedBookId}
                onChange={(e) => setSelectedBookId(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl text-sm font-medium appearance-none cursor-pointer transition-all
                  bg-[length:16px_16px] bg-[position:right_12px_center] bg-no-repeat
                  ${isDarkMode
                    ? 'bg-slate-800/60 border border-slate-700 text-white focus:border-amber-500'
                    : 'bg-white border border-slate-200 text-slate-900 focus:border-amber-500'
                  } focus:outline-none focus:ring-2 focus:ring-amber-500/20`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${isDarkMode ? '%2394a3b8' : '%2364748b'}' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                }}
              >
                {books.map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Stats line */}
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-5 px-1">
              {readyCount} of {chapters.length} chapters ready
            </p>

            {/* Chapter list */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedBookId}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                {chapters.length === 0 ? (
                  <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-10">
                    No chapters detected yet.
                  </p>
                ) : (
                  chapters.map((chapter, index) => {
                    const bookNotes = generatedNotes[selectedBookId] || {};
                    const existingNote = bookNotes[chapter.chapter_number];
                    const genKey = `${selectedBookId}-${chapter.chapter_number}`;
                    const isGenerating = generatingKey === genKey || existingNote?.status === 'generating' || existingNote?.status === 'pending';
                    const isCompleted = existingNote?.status === 'completed';
                    const isFailed = existingNote?.status === 'failed';

                    return (
                      <motion.div
                        key={chapter.chapter_number}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: index * 0.03 }}
                      >
                        {/* Ready state — tappable card */}
                        {!isGenerating && isCompleted && (
                          <div
                            onClick={() => handleView(existingNote)}
                            className={`rounded-xl border-l-4 border-l-emerald-500 px-4 py-4 cursor-pointer transition-all active:scale-[0.98]
                              ${isDarkMode
                                ? 'bg-slate-800/50 border border-slate-700/60 border-l-emerald-500 hover:bg-slate-800/70'
                                : 'bg-white border border-slate-200 border-l-emerald-500 hover:shadow-md'
                              }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">
                                  Ch.{chapter.chapter_number} &middot; {chapter.chapter_name || `Chapter ${chapter.chapter_number}`}
                                </p>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <CheckCircleIcon className="h-3.5 w-3.5 text-emerald-500" />
                                  <span className="text-xs text-emerald-600 dark:text-emerald-400">Ready</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleShare(existingNote, chapter.chapter_name); }}
                                  className="p-2 rounded-lg hover:bg-[#25D366]/10 transition-colors"
                                  title="Share on WhatsApp"
                                >
                                  <ShareIcon className="h-4 w-4 text-[#25D366]" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleGenerate(selectedBookId, chapter.chapter_number); }}
                                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                  title="Regenerate"
                                >
                                  <ArrowPathIcon className="h-3.5 w-3.5 text-slate-400" />
                                </button>
                                <ChevronRightIcon className="h-4 w-4 text-slate-400" />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Generating state */}
                        {isGenerating && (
                          <div className={`rounded-xl px-4 py-4
                            ${isDarkMode
                              ? 'bg-slate-800/50 border border-amber-500/20'
                              : 'bg-amber-50/50 border border-amber-200/60'
                            }`}
                          >
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">
                              Ch.{chapter.chapter_number} &middot; {chapter.chapter_name || `Chapter ${chapter.chapter_number}`}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <ArrowPathIcon className="h-3.5 w-3.5 text-amber-500 animate-spin" />
                              <span className="text-xs text-amber-600 dark:text-amber-400">Generating your playbook...</span>
                            </div>
                            {/* Shimmer progress bar */}
                            <div className="mt-3 h-1 rounded-full overflow-hidden bg-amber-100 dark:bg-amber-900/30">
                              <div className="h-full w-1/2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 animate-[shimmer_1.5s_ease-in-out_infinite]"
                                style={{ animation: 'shimmer 1.5s ease-in-out infinite' }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Not generated state */}
                        {!isGenerating && !isCompleted && !isFailed && (
                          <div className={`rounded-xl px-4 py-4
                            ${isDarkMode
                              ? 'bg-slate-800/30 border border-dashed border-slate-700/60'
                              : 'bg-slate-50/50 border border-dashed border-slate-200'
                            }`}
                          >
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate mb-3">
                              Ch.{chapter.chapter_number} &middot; {chapter.chapter_name || `Chapter ${chapter.chapter_number}`}
                            </p>
                            <button
                              onClick={() => handleGenerate(selectedBookId, chapter.chapter_number)}
                              className="w-full py-2.5 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white transition-colors flex items-center justify-center gap-2"
                            >
                              <SparklesIcon className="h-4 w-4" />
                              Generate Playbook
                            </button>
                          </div>
                        )}

                        {/* Failed state */}
                        {!isGenerating && isFailed && (
                          <div className={`rounded-xl px-4 py-4
                            ${isDarkMode
                              ? 'bg-slate-800/30 border border-red-500/20'
                              : 'bg-red-50/50 border border-red-200/60'
                            }`}
                          >
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">
                              Ch.{chapter.chapter_number} &middot; {chapter.chapter_name || `Chapter ${chapter.chapter_number}`}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-1.5">
                                <XMarkIcon className="h-3.5 w-3.5 text-red-500" />
                                <span className="text-xs text-red-500">Generation failed</span>
                              </div>
                              <button
                                onClick={() => handleGenerate(selectedBookId, chapter.chapter_number)}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
                              >
                                Retry
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })
                )}
              </motion.div>
            </AnimatePresence>
          </>
        )}
        {/* Shimmer animation */}
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(200%); }
          }
        `}</style>
      </div>
    </Layout>
  );
}
